import { useEffect, useState, useRef, useCallback } from "react";
import { client } from "../../utils/api";
import Layout from "../Layout/Layout";
import { BlogContent, BlogFeatured, BlogHeader } from "./components";
import { getCssVariable } from "src/utils/utils";
import { ClipLoader } from "react-spinners";

const ARTICLES_PER_PAGE = 6;

const BlogPage = () => {
  const gqlClient = client;
  const primaryColor = getCssVariable("--primary-color");
  const loadMoreRef = useRef(null);
  const featuredBlogIdRef = useRef<string | null>(null);

  const [blogPageHeaderData, setBlogPageHeaderData] = useState<{
    tagTitle: string;
    headerText?: string | null;
    backgroundImage?: any;
  } | null>(null);

  const [blogFeaturedData, setBlogFeaturedData] = useState<any>();
  const [blogCardContentData, setBlogCardContentData] = useState<any>([]);
  const [totalArticlesLoaded, setTotalArticlesLoaded] = useState(0);
  const [totalArticlesCount, setTotalArticlesCount] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [seoData, setSeoData] = useState<any>();
  const [loadedArticleIds, setLoadedArticleIds] = useState(new Set<string>());

  const fetchBlogPageData = async (skip = 0, limit = ARTICLES_PER_PAGE) => {
    if (!hasMore || (skip > 0 && loadingMore)) return;

    if (skip === 0) {
      setInitialLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const blogIdResponse = await gqlClient.GetBlogSectionIDs();
      
      const blogId =
        blogIdResponse?.blogSectionCollection?.items[0]?.sys.id ?? "";

      const blogPageResponse = await gqlClient.GetBlogPage({ id: blogId });
      
      const dataArticle = await gqlClient.GetArticles({ limit, skip });

      const featuredBlog = blogPageResponse?.blogSection?.featuredBlog;

      // Store featured blog ID for consistent filtering
      if (featuredBlog?.sys?.id && skip === 0) {
        featuredBlogIdRef.current = featuredBlog.sys.id;
      }

      // Filter out the featured blog
      const filteredArticles =
        dataArticle?.articleCollection?.items?.filter(
          (article) => article?.sys?.id !== featuredBlogIdRef.current
        ) ?? [];

      // Further filter out already loaded articles
      const uniqueArticles = filteredArticles.filter(
        (article) => article?.sys?.id && !loadedArticleIds.has(article.sys.id)
      );

      // If we're getting fewer unique articles than requested, we might be nearing the end
      if (uniqueArticles.length === 0) {
        setHasMore(false);
      }

      // Track which article IDs we've already loaded
      const newLoadedIds = new Set(loadedArticleIds);
      uniqueArticles.forEach((article) => {
        if (article?.sys?.id) {
          newLoadedIds.add(article.sys.id);
        }
      });

      setLoadedArticleIds(newLoadedIds);

      if (skip === 0) {
        setBlogPageHeaderData({
          tagTitle: blogPageResponse.blogSection?.eyebrowText ?? "",
          headerText: blogPageResponse.blogSection?.title,
          backgroundImage: blogPageResponse.blogSection?.backgroundImage,
        });

        setBlogFeaturedData(featuredBlog);
        setBlogCardContentData(uniqueArticles);
        setSeoData(blogPageResponse.blogSection?.seoFields);
        const totalCount = dataArticle?.articleCollection?.items?.length ?? 0;
        setTotalArticlesCount(totalCount);
        setTotalArticlesLoaded(uniqueArticles.length);
      } else {
        setBlogCardContentData((prev) => [...prev, ...uniqueArticles]);
        setTotalArticlesLoaded((prev) => prev + uniqueArticles.length);
      }
    } catch (error) {
      console.error("Error fetching blog page data:", error);
      setHasMore(false);
    } finally {
      if (skip === 0) {
        setInitialLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  };

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && hasMore && !loadingMore && !initialLoading) {
        fetchBlogPageData(totalArticlesLoaded);
      }
    },
    [totalArticlesLoaded, hasMore, loadingMore, initialLoading]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "20px",
      threshold: 0.1,
    });

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [handleObserver]);

  useEffect(() => {
    fetchBlogPageData();
  }, []);

  return (
    <Layout
      style={{ color: "#fff" }}
      hamburgerStyle={{ backgroundColor: primaryColor }}
      saleClassName="btn-gradient-bdr"
      headerStyle={{ position: "relative" }}
      loading={initialLoading}
      seoData={seoData}
     >
      <>
        <BlogHeader blogPageHeaderData={blogPageHeaderData} />
        <div className="all-blogs-section">
          <div className="container-fluid custom-spacer-col blog-space">
            <div className="blog-inner">
              {blogFeaturedData && <BlogFeatured content={blogFeaturedData} />}
              {blogCardContentData && (
                <BlogContent
                  content={blogCardContentData}
                  totalArticlesLoaded={totalArticlesCount}
                />
              )}
              <div ref={loadMoreRef} className="loading-container">
                {loadingMore && <ClipLoader color={primaryColor} size={40} />}
              </div>
            </div>
          </div>
        </div>
      </>
    </Layout>
  );
};

export default BlogPage;
